import {ParameterValidator} from '@mvps-genesis/utilities';

export default class PDFViewer {

	constructor (viewerUrl, allowDragging) {
		this.validator = new ParameterValidator(this);
		this.pdfs = {};

		this.viewerUrl = this.validator.isString('viewerUrl', viewerUrl);
		this.allowDragging = this.validator.isBoolean('allowDragging', allowDragging);

		this.elements = {
			closeTriggers: this.validator.isElementList('.js-pdf-close', '.js-pdf-close'),
			contentElem: this.validator.isElement('#js-pdf-content', '#js-pdf-content'),
			currentPDFTitles: this.validator.isElementList('.js-pdf-current-title', '.js-pdf-current-title'),
			iframeElemWrap: this.validator.isElement('#js-pdf-iframe-wrap', '#js-pdf-iframe-wrap'),
			minimizeTriggers: this.validator.isElementList('.js-pdf-minimize', '.js-pdf-minimize'),
			pdfTriggers: this.validator.isElementList('.js-preview-manual', '.js-preview-manual'),
			pillElem: this.validator.isElement('#js-pdf-pill', '#js-pdf-pill'),
			pillTitle: this.validator.isElement('#js-pdf-pill-title', '#js-pdf-pill-title'),
			wrapElem: this.validator.isElement('#js-pdf-wrap', '#js-pdf-wrap')
		};

		this.currentPDFName = '';
		this.pdfUrl = '';
		this.page = 0;

		this.isClosed = 1;
		this.isMinimized = 1;

		this.init();
		this.bindEvents();
	}

	init () {

		for (let pdfTrigger of this.elements.pdfTriggers) {
			this.insertPDFtoList(pdfTrigger);
		}

	}

	bindEvents () {

		for (let pdfTrigger of this.elements.pdfTriggers) {
			pdfTrigger.addEventListener('click', (e) => this.pdfTriggerClick(e, pdfTrigger), false);
		}

		for (let minimizeTrigger of this.elements.minimizeTriggers) {
			minimizeTrigger.addEventListener('click', () => this.minimize(), false);
		}

		for (let closeTrigger of this.elements.closeTriggers) {
			closeTrigger.addEventListener('click', () => this.close(), false);
		}

		this.elements.pillElem.addEventListener('click', () => this.toggleMinimized(), false);

		if (this.allowDragging) {
			this.enableDragging();
		}
	}

	pdfTriggerClick (e, trigger) {
		e.preventDefault();

		const slug = trigger.getAttribute('data-slug');
		const pageNumber = trigger.getAttribute('data-page-number');

		if (!this.pdfs.hasOwnProperty(slug)) {
			return false;
		}

		this.updatePDF(this.pdfs[slug].name, this.pdfs[slug].pdfUrl, pageNumber);

		this.maximize();

		return true;
	}

	insertPDFtoList (trigger) {
		const pdfName = trigger.getAttribute('data-name');
		const pdfUrl = trigger.getAttribute('data-pdf');
		const slugifiedPDF = this.constructor.slugify(pdfUrl);

		if (!this.pdfs.hasOwnProperty(slugifiedPDF)) {
			this.pdfs[slugifiedPDF] = {};
			this.pdfs[slugifiedPDF].pdfUrl = pdfUrl;
			this.pdfs[slugifiedPDF].name = pdfName;
		}

		trigger.setAttribute('data-slug', slugifiedPDF);
	}

	maximize () {
		this.isMinimized = 0;

		this.setIframe();

		this.elements.wrapElem.classList.add('t-pdf-wrap--shown');
		this.elements.wrapElem.classList.add('t-pdf-wrap--open');
	}

	minimize () {
		this.isMinimized = 1;

		this.elements.wrapElem.classList.remove('t-pdf-wrap--open');
	}

	close () {
		this.isMinimized = 1;
		this.isClosed = 1;

		this.elements.wrapElem.classList.remove('t-pdf-wrap--shown');
		this.elements.wrapElem.classList.remove('t-pdf-wrap--open');

		this.resetDragging();
	}

	resetDragging () {
		this.elements.contentElem.style.top = null;
		this.elements.contentElem.style.left = null;
		this.elements.contentElem.classList.remove('is-dragging');
	}

	toggleMinimized () {
		if (this.isMinimized === 1) {
			this.maximize();
		} else {
			this.minimize();
		}
	}

	setIframe () {
		let iframe = this.elements.iframeElemWrap.querySelector('iframe');
		const currentFile = iframe ? iframe.getAttribute('data-file') : '';
		let currentPage = iframe ? parseFloat(iframe.getAttribute('data-page')) : 0;

		if (isNaN(currentPage)) {
			currentPage = 0;
		}

		if (iframe && currentFile === this.pdfUrl && (currentPage === this.page || this.page === 0)) {
			return true;
		}

		if (!iframe) {
			iframe = document.createElement('iframe');
			iframe.classList.add('t-pdf-iframe');
			this.elements.iframeElemWrap.appendChild(iframe);
		}

		iframe.setAttribute('data-file', this.pdfUrl);
		iframe.setAttribute('data-page', this.page);

		// If the current file is the same, but the pages are different, only uploade the href.
		if (iframe.src && iframe.contentWindow) {
			iframe.contentWindow.location.href = this.viewerUrl + 'file=' + encodeURI(this.pdfUrl) + (this.page > 0 ? '#page=' + this.page : '');
		} else {
			iframe.src = this.viewerUrl + 'file=' + encodeURI(this.pdfUrl) + (this.page > 0 ? '#page=' + this.page : '');
		}

		return true;
	}

	updatePDF (name, pdf, page) {
		this.setCurrentPDFName(name);
		this.setPDFUrl(pdf);
		this.setPage(page);
	}

	setCurrentPDFName (name) {
		this.currentPDFName = name;
		this.elements.pillTitle.textContent = this.currentPDFName;

		for (const currentPDFTitle of this.elements.currentPDFTitles) {
			currentPDFTitle.textContent = this.currentPDFName;
		}
	}

	setPDFUrl (url) {
		this.pdfUrl = url;
	}

	setPage (page) {
		this.page = parseFloat(page);

		if (isNaN(this.page)) {
			this.page = 0;
		}
	}

	static slugify (string) {
		return string
			.toString()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase()
			.trim()
			.replace(/\s+/g, '-')
			.replace(/[^\w-]+/g, '')
			.replace(/--+/g, '-');
	}

	enableDragging () {
		const draggableArea = this.elements.contentElem.querySelector('.t-pdf-content-topbar');

		if (!draggableArea) {
			return;
		}

		this.dragPos1 = 0;
		this.dragPos2 = 0;
		this.dragPos3 = 0;
		this.dragPos4 = 0;
		this.isDragging = 0;

		draggableArea.addEventListener('mousedown', (e) => this.dragMouseDown(e));
		document.addEventListener('mouseup', () => this.closeDragElement());
		document.addEventListener('mousemove', (e) => this.elementDrag(e));
	}

	dragMouseDown (event) {
		let e = event || window.event;
		e.preventDefault();

		this.isDragging = 1;

		this.dragPos3 = e.clientX;
		this.dragPos4 = e.clientY;
	}

	elementDrag (event) {
		if (this.isDragging === 1) {
			let e = event || window.event;
			e.preventDefault();

			this.dragPos1 = this.dragPos3 - e.clientX;
			this.dragPos2 = this.dragPos4 - e.clientY;
			this.dragPos3 = e.clientX;
			this.dragPos4 = e.clientY;

			this.elements.contentElem.style.top = this.elements.contentElem.offsetTop - this.dragPos2 + 'px';
			this.elements.contentElem.style.left = this.elements.contentElem.offsetLeft - this.dragPos1 + 'px';
			this.elements.contentElem.classList.add('is-dragging');
		}
	}

	closeDragElement () {
		this.isDragging = 0;
	}

}
